// Non-iOS fallback renderer using Google's <model-viewer> custom element.
//
// model-viewer is loaded via a CDN <script type="module"> tag in index.html
// so it is *not* in the JS bundle. By the time this module renders, the
// element may still be upgrading; using attributes on a vanilla `createElement`
// works correctly because custom-element upgrades pick up the existing
// attributes on connect.
//
// The `ar` attribute enables Scene Viewer on Android Chrome and WebXR where
// available. On unsupported browsers model-viewer falls back to plain orbit/
// zoom which is exactly what we want.

import type { SharePayload } from "../types.ts";
import { renderError } from "./error-states.ts";

export function renderFallbackViewer(payload: SharePayload): HTMLElement {
  const container = document.createElement("section");
  container.className = "dm-viewer";
  container.setAttribute("data-testid", "dm-fallback-viewer");

  // Prefer GLB on non-iOS — it's smaller and renders faster in WebGL.
  // USDZ is the iOS-native format; model-viewer can technically decode it
  // but only via a heavyweight path. Falling back to USDZ when no GLB is
  // available still works for orbit/zoom; AR on Android will be limited.
  const src = payload.glbUrl ?? payload.usdzUrl;
  if (!src) {
    return renderError(
      "We can't load this deck",
      "The 3D preview for this estimate is missing. Ask your contractor to re-send the link.",
    );
  }

  const viewer = document.createElement("model-viewer");
  viewer.className = "dm-viewer__canvas";
  viewer.setAttribute("data-testid", "dm-model-viewer");
  viewer.setAttribute("src", src);
  if (payload.usdzUrl) {
    // Lets model-viewer hand off to AR Quick Look when iOS users somehow
    // end up on the fallback path (e.g. iPadOS in desktop-class browsing).
    viewer.setAttribute("ios-src", payload.usdzUrl);
  }
  viewer.setAttribute("alt", `3D preview of ${payload.deckName}`);
  viewer.setAttribute("ar", "");
  viewer.setAttribute("ar-modes", "scene-viewer webxr quick-look");
  viewer.setAttribute("camera-controls", "");
  viewer.setAttribute("touch-action", "pan-y");
  viewer.setAttribute("auto-rotate", "");
  viewer.setAttribute("shadow-intensity", "1");
  viewer.setAttribute("environment-image", "neutral");
  viewer.setAttribute("loading", "eager");
  if (payload.heroImageUrl) {
    viewer.setAttribute("poster", payload.heroImageUrl);
  }
  container.appendChild(viewer);

  return container;
}
